import React from "react"
import styled from "styled-components"
import NumberFormat from "react-number-format"
import { Link, generatePath } from "react-router-dom"
import { ROUTES } from "../../common/utils"
import { TickerInput } from "../../common/FormFields"
import { CloseIcon, PriceTagIcon } from "../../common/Icons"

const StyledTickerInput = styled.span`
  .TickerInput {
    display: block;
    position: relative;
    width: 60px;
    margin: 0 auto;
  }
  .TickerInput input {
    border: 1px solid #ddd;
    width: 100%;
    text-align: left;
  }
  .TickerInput button {
    border: none;
    height: 15px;
    position: absolute;
    top: 0;
    right: 0;
    box-shadow: none;
    background: transparent;
    line-height: 0;
  }
  .TickerInput button.inactive {
    opacity: 0.5;
  }
  .TickerInput button svg {
    transform: rotate(-90deg);
    fill: #ccc;
    margin-top: 2px;
  }
  .TickerInput button:first-child {
    top: 15px;
  }
  .TickerInput button:first-child svg {
    transform: rotate(90deg);
  }
  .TickerInput .ErrorBox {
    border: solid 1px #269fcb;
    background-color: #269fcb;
  }
  .TickerInput .ErrorBox::before {
    border: solid 10px #269fcb;
    border-bottom-color: transparent;
    border-right-color: transparent;
    border-left-color: transparent;
  }
`

const StyledCloseButton = styled.span`
  button {
    width: 30px;
    height: 30px;
    padding: 9px;
    background: transparent;
    border: none;
    box-shadow: none;
    display: block;
    line-height: 0;
  }
  button svg {
    width: 12px;
    height: 12px;
    fill: #656565;
  }
`

const StyledProductItem = styled.div`
  display: grid;
  grid-template-columns: 120px 4fr 2fr 2fr 150px 80px;
  text-align: center;
  margin: 20px 0;
  padding-bottom: 20px;
  border-bottom: 1px solid #d9d9d9;
  > span {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }
  .product-image {
    overflow: hidden;
    align-items: flex-start;
    img {
      width: 120px;
      height: 120px;
      object-fit: cover;
      object-position: center center;
    }
    .no-image {
      width: 120px;
      height: 120px;
      border: 1px solid #d9d9d9;
      svg {
        width: 50%;
        margin: 25%;
        fill: #d9d9d9;
      }
    }
  }
  .product-name {
    text-align: left;
    align-items: flex-start;
    padding-left: 10px;
    strong {
      display: block;
    }
  }
  .close-button {
    align-items: flex-end;
  }
  @media screen and (max-width: 1000px) {
    display: none;
  }
`

const StyledMobileProductItem = styled.div`
  display: none;
  position: relative;
  max-width: 90%;
  margin: 20px auto;
  border-top: 1px solid #d9d9d9;
  .close-button {
    position: absolute;
    top: 10px;
    right: 10px;
  }
  .product-display {
    padding-top: 40px;
    display: flex;
    justify-content: flex-start;
    .product-image {
      overflow: hidden;
      display: flex;
      align-items: flex-start;
      flex-grow: 0;
      margin-right: 10px;
      img {
        width: 80px;
        height: 80px;
        object-fit: cover;
        object-position: center center;
      }
      .no-image {
        width: 120px;
        height: 120px;
        border: 1px solid #d9d9d9;
        svg {
          width: 50%;
          margin: 25%;
          fill: #d9d9d9;
        }
      }
    }
  }
  .product-name {
    flex-grow: 1;
    strong {
      display: block;
    }
  }
  .product-info {
    display: flex;
    justify-content: space-between;
    height: 40px;
    align-items: center;
    .TickerInput input {
      margin-bottom: 0;
    }
  }
  @media screen and (max-width: 1000px) {
    display: block;
  }
`

function ProductItem({
  product,
  quantity,
  index,
  setQuantity,
  removeProduct,
  currency
}) {
  function handleSetQuantity(value) {
    setQuantity(value, index)
  }

  function handleRemoveProduct() {
    removeProduct(product.id)
  }

  return (
    <>
      <StyledMobileProductItem className="mobile">
        <div className="product-display">
          <span className="product-image">
            {product.image ? (
              <img
                src={product.image}
                alt={product.name}
                title={product.name}
              />
            ) : (
              <div className="no-image">
                <PriceTagIcon />
              </div>
            )}
          </span>
          <span className="product-name">
            <strong>
              <Link
                style={{ color: "#898989" }}
                to={{
                  pathname: generatePath(ROUTES.CHECKOUT_PRODUCT_DETAILS, {
                    product_id: product.id,
                    type: "regular"
                  })
                }}
              >
                {product.name}
              </Link>
            </strong>
            {product.variant !== "default_option" ? product.variant : null}
          </span>
        </div>
        <div className="product-info product-price">
          <span>Unit Price</span>
          <span>
            <NumberFormat
              value={parseFloat(product.price)}
              thousandSeparator={true}
              allowNegative={false}
              decimalScale={2}
              fixedDecimalScale={true}
              displayType="text"
              renderText={val => <>{val}</>}
            />{" "}
            {currency}
          </span>
        </div>
        <div className="product-info product-quantity">
          <span>Quantity</span>
          <span>
            <StyledTickerInput>
              <TickerInput
                min={1}
                max={parseInt(product.inventory_quantity)}
                value={quantity}
                setValue={handleSetQuantity}
                handleChange={handleSetQuantity}
                showMaxError={true}
                showMinError={true}
              />
            </StyledTickerInput>
          </span>
        </div>
        <div className="product-info product-quantity">
          <span>Total Price</span>
          <span>
            <NumberFormat
              value={parseInt(quantity) * parseFloat(product.price)}
              thousandSeparator={true}
              allowNegative={false}
              decimalScale={2}
              fixedDecimalScale={true}
              displayType="text"
              renderText={val => <>{val}</>}
            />{" "}
            {currency}
          </span>
        </div>
        <span className="close-button">
          <StyledCloseButton>
            <button type="button" onClick={handleRemoveProduct}>
              <CloseIcon />
            </button>
          </StyledCloseButton>
        </span>
      </StyledMobileProductItem>
      <StyledProductItem className="ProductItem desktop">
        <span className="product-image">
          {product.image ? (
            <img src={product.image} alt={product.name} title={product.name} />
          ) : (
            <div className="no-image">
              <PriceTagIcon />
            </div>
          )}
        </span>
        <span className="product-name">
          <strong>
            <Link
              style={{ color: "#898989" }}
              to={{
                pathname: generatePath(ROUTES.CHECKOUT_PRODUCT_DETAILS, {
                  product_id: product.snapshot_id,
                  type: "regular"
                })
              }}
            >
              {product.name}
            </Link>
          </strong>
          {product.variant !== "default_option" ? product.variant : null}
        </span>
        <span>
          <NumberFormat
            value={parseFloat(product.price)}
            thousandSeparator={true}
            allowNegative={false}
            decimalScale={2}
            fixedDecimalScale={true}
            displayType="text"
            renderText={val => <>{val}</>}
          />{" "}
          {currency}
        </span>
        <span>
          <StyledTickerInput>
            <TickerInput
              min={1}
              max={parseInt(product.inventory_quantity)}
              value={quantity}
              setValue={handleSetQuantity}
              handleChange={handleSetQuantity}
              showMaxError={true}
              showMinError={true}
            />
          </StyledTickerInput>
        </span>
        <span>
          <NumberFormat
            value={parseInt(quantity) * parseFloat(product.price)}
            thousandSeparator={true}
            allowNegative={false}
            decimalScale={2}
            fixedDecimalScale={true}
            displayType="text"
            renderText={val => <>{val}</>}
          />{" "}
          {currency}
        </span>
        <span className="close-button">
          <StyledCloseButton>
            <button type="button" onClick={handleRemoveProduct}>
              <CloseIcon />
            </button>
          </StyledCloseButton>
        </span>
      </StyledProductItem>
    </>
  )
}

export default ProductItem
